import type { GlobalThemeOverrides, ThemeCommonVars } from "naive-ui"
import tokens from "@/design-tokens.json"
import { Layout, RouterTransition, ThemeNameEnum } from "@/types/theme"
import { colorToArray, expandPattern, getColorAlphaShades, getThemeColors, getTypeValue } from "@/utils/theme"

type ThemeState = ReturnType<typeof getDefaultState>

interface ThemeGetters {
	naiveCommon: ThemeCommonVars
}

// const osTheme = useOsTheme()

export function getDefaultState() {
	return {
		layout: Layout.VerticalNav, // Type of layout, with vertical or horizontal navigation
		themeName: ThemeNameEnum.Light, // Theme name (Dark, Light), with fallback to the light theme
		routerTransition: RouterTransition.FadeUp, // Type of transition for the router
		routerTransitionDuration: 0.3, // Duration of the router transition in seconds
		rtl: false, // RTL (right to left) mode toggle
		boxed: {
			enabled: true, // Choose whether to apply a maximum width to the page
			toolbar: true, // Choose whether to apply the maximum width to the toolbar as well
			width: 1440 // Maximum width size in pixels
		},
		sidebar: {
			autoClose: true, // Choose whether to automatically close the sidebar when the view goes below the "autoCloseBreakpoint" value
			collapsed: false, // Indicates if the sidebar is collapsed
			autoCloseBreakpoint: 1000, // Breakpoint for the automatic closing of the sidebar (in pixels)
			animEase: "ease-in-out", // Type of easing for animations
			animDuration: 0.3, // Duration of sidebar animations (in seconds)
			openWidth: 300, // Width of the open sidebar (in pixels)
			closeWidth: 64 // Width of the closed sidebar (in pixels)
		},
		footer: {
			show: true // Show or hide the footer
		},
		responsive: {
			breakpoint: 700, // Breakpoint in pixels (Desk -> Mobile)
			// Parameters to be adjusted based on the breakpoint
			override: {
				viewPadding: {
					desk: 40, // View padding for desktop
					mobile: 20 // View padding for mobile devices
				},
				toolbarHeight: {
					desk: 80, // Height of the toolbar for desktop
					mobile: 70 // Height of the toolbar for mobile devices
				}
			}
		},
		toolbarHeight: 80, // Default toolbar height (in pixels)
		viewPadding: 40, // Default view padding (in pixels)
		headerBarHeight: 60, // Height of the header bar (in pixels)
		colors: tokens.colors, // Color definitions from the token
		cardPadding: tokens.cardPadding, // Cards padding from the token
		borderRadius: tokens.borderRadius, // Border radius from the token
		lineHeight: tokens.lineHeight, // Line height from the token
		fontSize: tokens.fontSize, // Font size from the token
		fontWeight: tokens.fontWeight, // Font weight from the token
		fontFamily: tokens.fontFamily, // Font family from the token
		typography: tokens.typography // Typography configurations from the token
	}
}

export function getThemeOverrides(state: ThemeState): GlobalThemeOverrides {
	const {
		primary,
		success,
		warning,
		error,
		info,
		background,
		backgroundSecondary,
		bodyBackground,
		text,
		textSecondary,
		textTertiary,
		textPlaceholder,
		border,
		hover
	} = state.colors[state.themeName]

	const themeColors = getThemeColors({ primary, success, warning, error, info })

	const lineHeight = state.lineHeight.default
	const borderRadius = state.borderRadius.default
	const borderRadiusSmall = state.borderRadius.small
	const borderColor = border
	const hoverColor = hover

	return {
		common: {
			...themeColors,
			textColorBase: text,
			textColor1: text,
			textColor2: text,
			textColor3: textTertiary,
			placeholderColor: textPlaceholder,
			bodyColor: bodyBackground,
			baseColor: background,
			popoverColor: background,
			cardColor: background,
			modalColor: background,
			lineHeight,
			borderRadius,
			borderRadiusSmall,
			fontSize: state.fontSize.default,
			fontWeight: state.fontWeight.default,
			fontWeightStrong: state.fontWeight.strong,
			fontFamily: state.fontFamily.default,
			fontFamilyMono: state.fontFamily.mono,
			borderColor,
			hoverColor,
			dividerColor: borderColor
		},
		Alert: {
			iconSize: "14px",
			padding: "10px",
			fontSize: state.fontSize.alert,
			border: "none",
			colorInfo: state.colors[state.themeName]["primary-200"],
			titleTextColorInfo: state.colors[state.themeName]["primary-700"],
			iconColorInfo: state.colors[state.themeName]["primary-700"],
			contentTextColorInfo: state.colors[state.themeName]["primary-700"],
			borderInfo: "none",
			colorError: state.colors[state.themeName]["error-400"],
			titleTextColorError: state.colors[state.themeName]["error-600"],
			iconColorError: state.colors[state.themeName]["error-600"],
			contentTextColorError: state.colors[state.themeName]["error-600"],
			borderError: "none"
		},
		Avatar: {
			color: state.colors[state.themeName]["grey-500"]
		},
		Button: {
			colorOpacitySecondary: 0.08,
			fontWeight: state.fontWeight.button,
			fontWeightStrong: state.fontWeight.buttonStrong,
			fontSize: state.fontSize.button,
			fontSizeSmall: state.fontSize.buttonSmall,
			fontSizeTiny: state.fontSize.buttonTiny,
			fontSizeLarge: state.fontSize.buttonLarge
		},
		Card: {
			color: background,
			borderRadius: state.borderRadius.cardMedium,
			colorEmbedded: backgroundSecondary,
			titleFontSizeSmall: state.fontSize.cardTitleSmall,
			titleFontSizeMedium: state.fontSize.cardTitleMedium,
			titleFontSizeLarge: state.fontSize.cardTitleLarge,
			titleFontSizeHuge: state.fontSize.cardTitleHuge,
			titleFontWeight: state.fontWeight.cardTitle,
			paddingSmall: state.cardPadding.small,
			paddingMedium: state.cardPadding.medium,
			paddingLarge: state.cardPadding.large,
			paddingHuge: state.cardPadding.huge
		},
		Dialog: {
			borderRadius: state.borderRadius.modal
		},
		Drawer: {
			borderRadius: 0,
			titleFontSize: state.fontSize.cardTitleMedium
		},
		Form: {
			labelTextColor: textSecondary,
			labelHeightLarge: 0,
			labelFontWeight: state.fontWeight.formLabel
		},
		LoadingBar: {
			colorLoading: primary
		},
		Scrollbar: {
			color: getColorAlphaShades(textTertiary)["050"]
		},
		Steps: {
			indicatorTextColorFinish: "white",
			indicatorBorderColorFinish: success,
			indicatorColorFinish: success,
			headerTextColorFinish: text,
			indicatorTextColorWait: text,
			indicatorBorderColorWait: text,
			headerTextColorWait: text,
			stepHeaderFontWeight: state.fontWeight.stepHeader,
			indicatorIndexFontSizeMedium: "14px",
			indicatorSizeMedium: "26px"
		},
		Tabs: {
			// tabFontWeightActive: state.fontWeight.tabActive,
			tabGapLargeLine: 0,
			panePaddingMedium: "24px 0 0"
		},
		Tag: {
			colorBordered: "rgba(0, 0, 0, 0.1)"
		},
		Typography: {
			headerFontSize1: getTypeValue(state, state.typography.h1.fontSize),
			headerFontSize2: getTypeValue(state, state.typography.h2.fontSize),
			headerFontSize3: getTypeValue(state, state.typography.h3.fontSize),
			headerFontSize4: getTypeValue(state, state.typography.h4.fontSize),
			headerFontSize5: getTypeValue(state, state.typography.h5.fontSize),
			headerFontSize6: getTypeValue(state, state.typography.h6.fontSize)
		}
	}
}

export function getCssVars(state: ThemeState, getters: ThemeGetters): { [key: string]: string } {
	const naive = getters.naiveCommon

	const bgColor = naive.baseColor
	const bgSecondaryColor = state.colors[state.themeName].backgroundSecondary
	const bgTertiaryColor = state.colors[state.themeName].backgroundTertiary
	const fgColor = state.colors[state.themeName].text
	const fgSecondaryColor = state.colors[state.themeName].textSecondary
	const fgTertiaryColor = state.colors[state.themeName].textTertiary
	const fgPlaceholderColor = state.colors[state.themeName].textPlaceholder

	const bgSidebar = state.colors[state.themeName].sidebarBackground
	const bgBody = state.colors[state.themeName].bodyBackground

	const boxedWidth = state.boxed.width
	const routerTransitionDuration = state.routerTransitionDuration
	const sidebarAnimEase = state.sidebar.animEase
	const sidebarAnimDuration = state.sidebar.animDuration
	const sidebarOpenWidth = state.sidebar.openWidth
	const sidebarCloseWidth = state.sidebar.closeWidth
	const toolbarHeight = state.toolbarHeight
	const viewPadding = state.viewPadding
	const headerBarHeight = state.headerBarHeight
	const fontFamily = state.fontFamily.default
	const fontFamilyDisplay = state.fontFamily.display
	const fontFamilyMono = state.fontFamily.mono
	const fontSize = state.fontSize.default
	const lineHeight = state.lineHeight.default

	const borderRadius = state.borderRadius.default
	const borderRadiusSmall = state.borderRadius.small
	const cardRadiusSmall = state.borderRadius.cardSmall
	const cardRadiusMedium = state.borderRadius.cardMedium
	const cardRadiusLarge = state.borderRadius.cardLarge
	const cardRadiusHuge = state.borderRadius.cardHuge

	const bezierEase = naive.cubicBezierEaseInOut

	// This style object, imported via the themeStore, will be available application-wide and is exposed in the HTML tag as a list of CSS variables, which you can use in your CSS/SCSS code like: var(--bg-default-color)
	const styleObject: Record<string, string> = {
		"bg-body-color": `${bgBody}`,
		"bg-sidebar-color": `${bgSidebar}`,

		"fg-default-color": `${fgColor}`,
		"fg-secondary-color": `${fgSecondaryColor}`,
		"fg-tertiary-color": `${fgTertiaryColor}`,
		"fg-placeholder-color": `${fgPlaceholderColor}`,
		"bg-default-color": `${bgColor}`,
		"bg-secondary-color": `${bgSecondaryColor}`,
		"bg-tertiary-color": `${bgTertiaryColor}`,

		"bezier-ease": `${bezierEase}`,
		"router-transition-duration": `${routerTransitionDuration}s`,
		"sidebar-anim-ease": `${sidebarAnimEase}`,
		"sidebar-anim-duration": `${sidebarAnimDuration}s`,
		"sidebar-open-width": `${sidebarOpenWidth}px`,
		"sidebar-close-width": `${sidebarCloseWidth}px`,
		"boxed-width": `${boxedWidth}px`,
		"toolbar-height": `${toolbarHeight}px`,
		"header-bar-height": `${headerBarHeight}px`,
		"view-padding": `${viewPadding}px`,

		"border-radius": `${borderRadius}`,
		"border-radius-small": `${borderRadiusSmall}`,
		"card-radius-small": `${cardRadiusSmall}`,
		"card-radius-medium": `${cardRadiusMedium}`,
		"card-radius-large": `${cardRadiusLarge}`,
		"card-radius-huge": `${cardRadiusHuge}`,

		"font-family": `${fontFamily}`,
		"font-family-display": `${fontFamilyDisplay}`,
		"font-family-mono": `${fontFamilyMono}`,
		"font-size": `${fontSize}`,
		"line-height": `${lineHeight}`
	}

	// import colors by patterns
	for (const pattern of [
		"extra(1|2|3|4)",
		"status-(pregnancy|fertility|menopause|abortion|postpartum)",
		"status-(pregnancy|fertility|menopause|abortion|postpartum)-(400|500|600)",
		"primary",
		"primary-(100|200|300|400|500|700)",
		"success",
		"success-(400|500|600)",
		"warning",
		"warning-(400|500|600)",
		"error",
		"error-(400|500|600)",
		"info",
		"info-(400|500|600)",
		"grey",
		"grey-(50|100|200|300|500|700|900)",
		"border",
		"hover"
	]) {
		const keys = expandPattern(pattern)

		for (const key of keys) {
			styleObject[`${key}-color`] = (state.colors[state.themeName] as Record<string, string>)[key] ?? ""
		}
	}

	// add RGB values variant
	for (const [key, value] of Object.entries(styleObject)) {
		if (key.endsWith("-color")) {
			styleObject[`${key}-rgb`] = colorToArray(value, "rgb").join(" ")
		}
	}

	return styleObject
}
